/*
 * Copyright (c) Microsoft Corporation.
 * Licensed under the MIT License.
 *
 * Code generated by Microsoft (R) AutoRest Code Generator.
 * Changes may cause incorrect behavior and will be lost if the code is regenerated.
 */

import * as coreClient from '@azure/core-client';
import * as coreRestPipeline from '@azure/core-rest-pipeline';

/** Information about a user. */
export interface User {
    /** Globally unique identifier of the user. */
    id: string;
    /** Linked GitHub account name. */
    githubLogin?: string;
}

/** User to delete. */
export interface DeleteCommand {
    /** Object Id of the user to delete. */
    oid: string;
    /** Tenant Id of the user to delete. */
    tid: string;
}

/** Describes the state of progress of a user through a learning course. */
export interface UserProgress {
    /** Globally unique identifier of the user this progress is for. */
    userId?: string;
    /** Globally unique identifier of the course this progress is for. */
    courseId?: string;
    /** Progress on course units. */
    units?: UnitProgress[];
}

/** Describes the state of progress of a user through a course unit. */
export interface UnitProgress {
    /** Globally unique identifier of a unit. */
    unitId: string;
    /** Progress on unit lessons. */
    lessons?: LessonProgress[];
}

/** Describes the state of progress of a user through a lesson. */
export interface LessonProgress {
    /** Globally unique identifier of the lesson. */
    lessonId: string;
    /** Progress on lesson parts. */
    parts?: LessonPartProgress[];
}

/** Describes the state of progress of a user through a lesson part. */
export interface LessonPartProgress {
    /** Globally unique identifier of the part. */
    partId: string;
    /** Completion percentage for the part. */
    percentComplete: number;
    /** Answers to lesson part questions. */
    questionAnswers?: QuestionAnswer[];
}

/** Single answer to a lesson part question. */
export interface QuestionAnswer {
    /** Globally unique identifier of the question. */
    questionId: string;
    /** Time the answer was recorded. */
    timestamp: Date;
    /**
     * Response to the question choices:
     * - String: array of strings for each element.
     * - Single choice: the 0-based index of selected option.
     * - Multiple choice: array of 0-based indices of selected options.
     * - Mapping: array of 0-based indices of choices for each element.
     * - Reflection: a string response.
     *
     */
    response: any;
}

/** User project metadata. */
export interface Project {
    /** Globally unique identifier of the project. */
    id?: string;
    /** Short identifier of the project within the user scope. */
    projectId?: string;
    /** Indicates primary code language used by the project (e.g. Python or HTML). */
    type: CodeLanguage;
    /** Title of the project. */
    title: string;
    /** Description of the project. */
    description?: string;
    /** Public URL if the project is published and null otherwise. */
    publicUrl?: string;
    /** Indicates whether the published app has been disabled due to policy violations. */
    publicUrlDisabled?: boolean;
    /** Indicates whether to republish the project on every update (for already published projects). */
    publishOnUpdate?: boolean;
    /** The last time the project was published. */
    lastPublished?: Date;
    /** The last time the project was updated. */
    lastUpdated?: Date;
    /** Indicates whether most recent update has been published. */
    lastUpdatePublished?: boolean;
    /** Unique identifier of the shared link. */
    sharedLinkId?: string;
    /** Tags associated with the project. */
    tags?: string[];
}

/** Response from CVS callback */
export interface CvsValidationResponse {
    data: Record<string, unknown>[];
    included?: Record<string, unknown>[];
}

/** Immersive Reader client configuration. */
export interface ImmersiveReaderConfig {
    /** Connection URL for the Immersive Reader. */
    endpoint: string;
    /** Authorization token to use when connecting to Immersive Reader. */
    token: string;
    /** Expiration time for the token, after which it has to be renewed. */
    expiresOn: Date;
}

/** Describes the state of a long-running operation. */
export interface Operation {
    /** Globally unique identifier of the operation. */
    id: string;
    /** Short identifier of the operation unique within the user scope. */
    operationId: string;
    /** Status of the operation. */
    status: OperationStatus;
    /** Time the operation was created. */
    created: Date;
    /** Time the operation was last updated. */
    lastUpdated: Date;
    /** Error that occurred during the operation. */
    error?: ErrorDetail;
}

/** Error details. */
export interface ErrorDetail {
    /** Short error code. */
    code: string;
    /** Error message. */
    message: string;
    /** Additional error details. */
    details?: ErrorDetail[];
}

/** Curriculum metadata. */
export interface Curriculum {
    /**
     * Globally unique identifier of the curriculum.
     * NOTE: This property will not be serialized. It can only be populated by the server.
     */
    readonly id: string;
    /**
     * Short identifier of the curriculum unique within the user scope.
     * NOTE: This property will not be serialized. It can only be populated by the server.
     */
    readonly curriculumId: string;
    /** Type of the curriculum. */
    type: CourseType;
    /** Platform the curriculum is designed for. */
    platform: CoursePlatform;
    /**
     * State of the curriculum.
     * NOTE: This property will not be serialized. It can only be populated by the server.
     */
    readonly state: CurriculumState;
    /** Title of the curriculum. */
    title: string;
    /** Description of the curriculum. */
    description?: string;
    /** Tags associated with the curriculum. */
    tags?: string[];
    /**
     * Name of the repository containing the curriculum.
     * NOTE: This property will not be serialized. It can only be populated by the server.
     */
    readonly repoName?: string;
    /**
     * Time the curriculum was created.
     * NOTE: This property will not be serialized. It can only be populated by the server.
     */
    readonly created: Date;
    /**
     * Time the curriculum was last updated.
     * NOTE: This property will not be serialized. It can only be populated by the server.
     */
    readonly lastUpdated: Date;
    /**
     * Time the curriculum was last published.
     * NOTE: This property will not be serialized. It can only be populated by the server.
     */
    readonly lastPublished?: Date;
    /**
     * For a published curriculum, the course id.
     * NOTE: This property will not be serialized. It can only be populated by the server.
     */
    readonly courseId?: string;
}

/** Group of education standards. */
export interface StandardGroup {
    /** Globally unique identifier of the group. */
    id: string;
    /** Display name of the group. */
    displayName: string;
    /** Title of the standard group. */
    title: string;
    /** Description of the group. */
    description?: string;
    /** Standards body contact. */
    author?: Contact;
    /** Usage license URL for the standards. */
    license?: string;
    /** Standards in this group. */
    standards?: Standard[];
}

/** Contact information for a person or organization. */
export interface Contact {
    /** Short display name of the contact. */
    displayName: string;
    /** Email address of the contact. */
    email?: string;
    /** Absolute or relative URL of the author photograph. */
    photo?: string;
    /** Description of institution the author is affiliated with. */
    affiliation?: string;
    /** Home page URL for the contact. */
    homePage?: string;
}

/** Education standard. */
export interface Standard {
    /** Globally unique identifier of the standard. */
    id: string;
    /** Display name of the standard. */
    displayName: string;
    /** Title of the standard. */
    title: string;
    /** Description of the standard. */
    description?: string;
    /** Target audience of the standard. */
    audience?: string;
}

/** Progression of courses on a particular subject. */
export interface LearningPath {
    /** Globally unique identifier of the learning path. */
    id: string;
    /** Language locale of the learning content. */
    locale: string;
    /** Absolute or relative URL of the learning path icon. */
    icon?: string;
    /** Title of the learning path. */
    title: string;
    /** Description of the learning path (text or Markdown). */
    description?: string;
    /** Path learning objectives (text or Markdown). */
    objectives?: string;
    /** Brand association of the learning path (text or Markdown). */
    brand: string;
    /** Level of difficulty of the path. */
    level?: DifficultyLevel;
    /** Prerequisites for starting on the learning path. */
    prerequisites?: Prerequisites;
    /** Ordered list of course IDs in the path. */
    courses: string[];
}

/** Specification of prerequisites for taking a section of learning content. */
export interface Prerequisites {
    /** Description of the prerequisites (text or Markdown). */
    description?: string;
    /** List of learning element IDs that are recommended for completion before starting on the content. */
    recommended?: string[];
    /** List of learning element IDs that must be completed before content is shown to the user. */
    hiddenBefore?: string[];
    /** List of learning element IDs that must be completed before content is unlocked for the user. */
    lockedBefore?: string[];
}

/** Learning course metadata. */
export interface Course {
    /** Globally unique identifier of the learning course. */
    id: string;
    /** Type of the learning course. */
    type: CourseType;
    /** Platform used in the course. */
    platform: CoursePlatform;
    /** Language locale of the learning content. */
    locale: string;
    /** Absolute or relative URL of the course card image. */
    cardImage?: string;
    /** Title of the learning course. */
    title: string;
    /** Description of the learning course (text or Markdown). */
    description?: string;
    /** Course learning objectives (text or Markdown). */
    objectives?: string;
    /** The authors or sponsors of the course's content. */
    authors?: Contact[];
    /** Level of difficulty of the course. */
    level?: DifficultyLevel;
    /** Prerequisites for starting on the learning course. */
    prerequisites?: Prerequisites;
    /** Estimated time required to take the course (in minutes). */
    duration?: number;
    /** Visual Studio Code profile to load for this course. */
    profileName?: string;
    /** Version of Python language/libraries used by the course. */
    pythonVersion?: string;
    /** Course units. */
    units?: Unit[];
    /** Url of the course glossary. */
    glossary?: string;
    /** Resources for educators. */
    educatorResources?: EducatorResource[];
    /** Tags associated with the course. */
    tags?: string[];
}

/** Course unit metadata. */
export interface Unit {
    /** Globally unique identifier of the unit. */
    id: string;
    /** Language locale of the learning content. */
    locale: string;
    /** Absolute or relative URL of the unit icon. */
    icon?: string;
    /** Title of the unit. */
    title: string;
    /** Description of the unit (text or Markdown). */
    description?: string;
    /** Unit learning objectives (text or Markdown). */
    objectives?: string;
    /** The primary author or sponsor of the unit's content. */
    author?: Contact;
    /** Level of difficulty of the unit. */
    level?: DifficultyLevel;
    /** Prerequisites for starting on the unit. */
    prerequisites?: Prerequisites;
    /** Estimated time required to take the unit (in minutes). */
    duration?: number;
    /** Ordinal value used to sort units in a course. */
    order: number;
    /** Unit lessons. */
    lessons?: Lesson[];
}

/** Lesson metadata. */
export interface Lesson {
    /** Globally unique identifier of the lesson. */
    id: string;
    /** Language locale of the learning content. */
    locale: string;
    /** Absolute or relative URL of the lesson icon. */
    icon?: string;
    /** Title of the lesson. */
    title: string;
    /** Description of the lesson (text or Markdown). */
    description?: string;
    /** Lesson learning objectives (text or Markdown). */
    objectives?: string;
    /** The primary author or sponsor of the lesson's content. */
    author?: Contact;
    /** Level of difficulty of the lesson. */
    level?: DifficultyLevel;
    /** Prerequisites for starting on the lesson. */
    prerequisites?: Prerequisites;
    /** List of IDs of education standards covered by the lesson. */
    standards?: string[];
    /** Estimated time required to take the lesson (in minutes). */
    duration?: number;
    /** Ordinal value used to sort lessons in a unit. */
    order: number;
    /** Editor windows configuration. */
    editors?: EditorSettings[];
    /** Terminal window configuration. */
    terminal?: TerminalSettings;
    /** Layout settings for the lesson. */
    layout?: LayoutSettings;
    /** Lesson parts. */
    parts?: LessonPart[];
    /** Resources for educators. */
    educatorResources?: EducatorResource[];
}

/** Editor window configuration. */
export interface EditorSettings {
    /** Indicates the language of the code editor that should be opened when user navigates to this lesson. */
    type: CodeLanguage;
    /** Path to a code file template that accompanies the lesson. */
    template?: string;
    /** Name or absolute path to the code file stored on the user drive. */
    filename?: string;
}

/** Terminal window configuration. */
export interface TerminalSettings {
    /** The kind of terminal that should be opened when user navigates to the containing element. */
    type: TerminalType;
    /** Determines if the terminal should be cleared when navigating to the containing element. */
    clear?: boolean;
}

/** IDE Layout settings. */
export interface LayoutSettings {
    /** Identifies the IDE components to be displayed. */
    components?: string;
    /** Determines if the file explorer should be displayed. */
    explorer?: boolean;
}

/** Lesson part metadata. */
export interface LessonPart {
    /** Globally unique identifier of the part. */
    id: string;
    /** Language locale of the learning content. */
    locale: string;
    /** Title of the lesson part. */
    title: string;
    /** Level of difficulty of the part. */
    level?: DifficultyLevel;
    /** Prerequisites for starting on the part. */
    prerequisites?: Prerequisites;
    /** Ordinal value used to sort parts in a lesson. */
    order: number;
    /** Editor windows configuration. */
    editors?: EditorSettings[];
    /** Terminal window configuration. */
    terminal?: TerminalSettings;
    /** Layout settings for the lesson part. */
    layout?: LayoutSettings;
    /** Absolute or relative URL of the part's content. */
    contentUri?: string;
    /** Quiz questions for the content presented. */
    quiz?: Quiz;
}

/** A set of questions to evaluate learning progress. */
export interface Quiz {
    /** Title of the quiz. */
    title: string;
    /** Path to a file with validation code. */
    validation?: string;
    /** Quiz questions for the content presented. */
    questions?: Question[];
}

/** Quiz or assessment question. */
export interface Question {
    /** Globally unique identifier of the question. */
    id: string;
    /** Question content displayed to the user (text or Markdown). */
    content: string;
    /** Hint content displayed to the user (text or Markdown). */
    hint?: string;
    /** Type of the question/response structure. */
    type: QuestionType;
    /** Whether to randomize the order of responses shown to the user. */
    scrambleOrder?: boolean;
    /** Whether to randomize the order of mapping choices shown to the user. */
    scrambleChoices?: boolean;
    /** List of mapping choices for mapping question type. */
    choices?: string[];
    /** Possible response options. */
    responses: QuestionResponse[];
}

/** Question response option. */
export interface QuestionResponse {
    /** Response option content displayed to the user (text or Markdown). */
    content?: string;
    /** Explanation of correct/incorrect answer displayed to the user (text or Markdown). */
    explanation?: string;
    /** Explanation of incorrect answer displayed to the user (text or Markdown). */
    incorrectExplanation?: string;
    /**
     * Indicates correct value for this response:
     * - String: expected string value
     * - Single choice: true/false
     * - Multiple choice: true/false
     * - Mapping: expected choice value or index
     * - Reflection: does not have a correct value so undefined is used here
     *
     */
    correct?: any;
}

/** Resource for educators. */
export interface EducatorResource {
    /** mime type of the resource. */
    type: string;
    /** Title of the resource. */
    title: string;
    /** Absolute or relative URL of the resource. */
    url: string;
}

/** Details on a reported piece of user generated content */
export interface UserContentReport {
    /** Location of the reported user generated content */
    url: string;
    /** Type of violation reported */
    abuseCategory: AbuseCategory;
    /** Description of what offended the user about this content */
    reason?: string;
    /** Name of the user who reported the content */
    reporterName?: string;
    /** Email address of the user who reported the content */
    reporterEmail?: string;
}

/** Project template metadata. */
export interface ProjectTemplate {
    /** Unique identifier of the project template. */
    id: string;
    /** Indicates primary code language used by the project template (e.g. Python or HTML). */
    type: CodeLanguage;
    /** Title of the project. */
    title: string;
    /** Description of the project. */
    description: string;
    /** Absolute or relative URL of the project template icon. */
    icon: string;
    /** Indicates if this project template is a featured project template. */
    featured?: boolean;
    /** Tags associated with the project template. */
    tags?: string[];
}

/** Shared link information */
export interface SharedLink {
    /** Unique shared link identifier */
    id: string;
    /** Shared link type */
    type: 'project';
    /** Identifier of the shared target depending on link type. */
    targetId: string;
}

/** Glossary of terms used in the course. */
export interface Glossary {
    /** Unique identifier of the glossary. */
    id: string;
    /** List of glossary terms. */
    terms: GlossaryTerm[];
}

/** A glossary term to search for in content as well as the tooltip content for that glossary term. */
export interface GlossaryTerm {
    /** The term to search for in content. */
    term: string;
    /** The markdown string tooltip content for the glossary term. */
    definition: string;
    /** Whether to ignore case when searching for the term in content. */
    ignoreCase?: boolean;
}

/** Defines headers for VsCodeEduClient_deleteUser operation. */
export interface VsCodeEduClientDeleteUserHeaders {
    /** URL to the operation status. */
    operationLocation?: string;
    /** Identifier of the operation. */
    operationId?: string;
}

/** Defines headers for VsCodeEduClient_deleteUserProject operation. */
export interface VsCodeEduClientDeleteUserProjectHeaders {
    /** URL to the operation status. */
    operationLocation?: string;
    /** Identifier of the operation. */
    operationId?: string;
}

/** Defines headers for VsCodeEduClient_publishUserProject operation. */
export interface VsCodeEduClientPublishUserProjectHeaders {
    /** URL to the operation status. */
    operationLocation?: string;
    /** Identifier of the operation. */
    operationId?: string;
}

/** Defines headers for VsCodeEduClient_unpublishUserProject operation. */
export interface VsCodeEduClientUnpublishUserProjectHeaders {
    /** URL to the operation status. */
    operationLocation?: string;
    /** Identifier of the operation. */
    operationId?: string;
}

/** Defines headers for VsCodeEduClient_createOrUpdateUserCurriculum operation. */
export interface VsCodeEduClientCreateOrUpdateUserCurriculumHeaders {
    /** URL to the operation status. */
    operationLocation?: string;
    /** Identifier of the operation. */
    operationId?: string;
}

/** Defines headers for VsCodeEduClient_deleteUserCurriculum operation. */
export interface VsCodeEduClientDeleteUserCurriculumHeaders {
    /** URL to the operation status. */
    operationLocation?: string;
    /** Identifier of the operation. */
    operationId?: string;
}

/** Defines headers for VsCodeEduClient_publishUserCurriculum operation. */
export interface VsCodeEduClientPublishUserCurriculumHeaders {
    /** URL to the operation status. */
    operationLocation?: string;
    /** Identifier of the operation. */
    operationId?: string;
}

/** Defines headers for VsCodeEduClient_unpublishUserCurriculum operation. */
export interface VsCodeEduClientUnpublishUserCurriculumHeaders {
    /** URL to the operation status. */
    operationLocation?: string;
    /** Identifier of the operation. */
    operationId?: string;
}

/** Defines headers for VsCodeEduClient_deleteCourse operation. */
export interface VsCodeEduClientDeleteCourseHeaders {
    /** URL to the operation status. */
    operationLocation?: string;
    /** Identifier of the operation. */
    operationId?: string;
}

/** Parameter group */
export interface StartSessionOptions {
    /** Name of the authentication provider */
    authProvider: AuthProvider;
    /** Redirect URL for authentication */
    redirectUrl: string;
    /** Whether the duration for the session token is long term */
    longTerm: boolean;
}

/** Parameter group */
export interface ExchangeSessionOptions {
    /** State for authentication */
    state: string;
    /** Access code for authentication */
    accessCode: string;
}

/** Parameter group */
export interface NewSessionOptions {
    /** Name of the authentication provider */
    authProvider: AuthProvider;
    /** Whether the duration for the session token is long term */
    longTerm: boolean;
    /** Access code for authentication */
    accessCode: string;
    /** Redirect URL for authentication */
    redirectUrl: string;
}

/** Parameter group */
export interface NewOtacOptions {
    /** Code challenge for PKCE authentication */
    codeChallenge?: string;
    /** Whether the duration for the session token is long term */
    longTerm: boolean;
}

/** Parameter group */
export interface ExchangeOtacOptions {
    /** OTAC for authentication */
    otac: string;
    /** Code verifier for PKCE authentication */
    codeVerifier?: string;
}

/** Parameter group */
export interface GithubInstallExchangeOptions {
    /** Access code for authentication */
    accessCode: string;
    /** State for authentication */
    state: string;
}

/** Known values of {@link HostOptions} that the service accepts. */
export enum KnownHostOptions {
    /** host: https:\//ppe.vscodeedu.com\/api */
    HttpsPpeVscodeeduComApi = 'https://ppe.vscodeedu.com/api',
    /** host: https:\//vscodeedu.com\/api */
    HttpsVscodeeduComApi = 'https://vscodeedu.com/api',
}

/**
 * Defines values for HostOptions. \
 * {@link KnownHostOptions} can be used interchangeably with HostOptions,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **https:\//ppe.vscodeedu.com\/api**: host: https:\/\/ppe.vscodeedu.com\/api \
 * **https:\//vscodeedu.com\/api**: host: https:\/\/vscodeedu.com\/api
 */
export type HostOptions = string;
/** Defines values for CodeLanguage. */
export type CodeLanguage =
    | 'abap'
    | 'bat'
    | 'bibtex'
    | 'clojure'
    | 'coffeescript'
    | 'c'
    | 'cpp'
    | 'csharp'
    | 'dockercompose'
    | 'css'
    | 'cuda-cpp'
    | 'diff'
    | 'dockerfile'
    | 'fsharp'
    | 'git-commit'
    | 'git-rebase'
    | 'go'
    | 'groovy'
    | 'handlebars'
    | 'haml'
    | 'html'
    | 'ini'
    | 'java'
    | 'javascript'
    | 'javascriptreact'
    | 'json'
    | 'jsonc'
    | 'latex'
    | 'less'
    | 'lua'
    | 'makefile'
    | 'markdown'
    | 'objective-c'
    | 'objective-cpp'
    | 'perl'
    | 'perl6'
    | 'php'
    | 'plaintext'
    | 'powershell'
    | 'jade'
    | 'pug'
    | 'python'
    | 'r'
    | 'razor'
    | 'ruby'
    | 'rust'
    | 'scss'
    | 'sass'
    | 'shaderlab'
    | 'shellscript'
    | 'slim'
    | 'sql'
    | 'stylus'
    | 'swift'
    | 'typescript'
    | 'typescriptreact'
    | 'tex'
    | 'vb'
    | 'vue'
    | 'vue-html'
    | 'xml'
    | 'xsl'
    | 'yaml';
/** Defines values for OperationStatus. */
export type OperationStatus = 'not-started' | 'running' | 'succeeded' | 'failed';
/** Defines values for CourseType. */
export type CourseType = 'course' | 'coding-activity';
/** Defines values for CoursePlatform. */
export type CoursePlatform = 'python' | 'html';
/** Defines values for CurriculumState. */
export type CurriculumState =
    | 'creating'
    | 'draft'
    | 'updating'
    | 'publishing'
    | 'published'
    | 'unpublishing'
    | 'deleting';
/** Defines values for DifficultyLevel. */
export type DifficultyLevel = 'beginner' | 'intermediate' | 'advanced' | 'expert';
/** Defines values for TerminalType. */
export type TerminalType = 'none' | 'python';
/** Defines values for QuestionType. */
export type QuestionType = 'string' | 'choice' | 'multiple' | 'mapping' | 'reflection';
/** Defines values for AbuseCategory. */
export type AbuseCategory =
    | 'AdultNudity'
    | 'AlcoholTobaccoDrugs'
    | 'ChildEndangermentExploitation'
    | 'ContentInfringement'
    | 'CoordinationOfHarm'
    | 'Fraud'
    | 'GraphicViolenceHumanGore'
    | 'HateSpeech'
    | 'ImminentHarmToPersonsOrProperty'
    | 'NonConsensualAdultImages'
    | 'OffensiveContent'
    | 'ProfanityVulgarity'
    | 'SexualSolicitation'
    | 'Spam'
    | 'SuicideSelfHarm'
    | 'Terrorism'
    | 'ThreatsCyberbullyingHarassment'
    | 'ThreatsOrPraiseOfViolence'
    | 'Trafficking'
    | 'VirusSpywareMalware'
    | 'Other';
/** Defines values for AuthProvider. */
export type AuthProvider = 'microsoft' | 'google' | 'clever';

/** Optional parameters. */
export interface GetUserOptionalParams extends coreClient.OperationOptions {}

/** Contains response data for the getUser operation. */
export type GetUserResponse = User;

/** Optional parameters. */
export interface DeleteUserOptionalParams extends coreClient.OperationOptions {}

/** Contains response data for the deleteUser operation. */
export type DeleteUserResponse = VsCodeEduClientDeleteUserHeaders;

/** Optional parameters. */
export interface NgpDeleteUsersOptionalParams extends coreClient.OperationOptions {}

/** Optional parameters. */
export interface DownloadUserDataOptionalParams extends coreClient.OperationOptions {}

/** Contains response data for the downloadUserData operation. */
export type DownloadUserDataResponse = {
    /**
     * BROWSER ONLY
     *
     * The response body as a browser Blob.
     * Always `undefined` in node.js.
     */
    blobBody?: Promise<Blob>;
    /**
     * NODEJS ONLY
     *
     * The response body as a node.js Readable stream.
     * Always `undefined` in the browser.
     */
    readableStreamBody?: NodeJS.ReadableStream;
};

/** Optional parameters. */
export interface DeleteUserDriveOptionalParams extends coreClient.OperationOptions {}

/** Optional parameters. */
export interface DownloadUserDriveOptionalParams extends coreClient.OperationOptions {}

/** Contains response data for the downloadUserDrive operation. */
export type DownloadUserDriveResponse = {
    /**
     * BROWSER ONLY
     *
     * The response body as a browser Blob.
     * Always `undefined` in node.js.
     */
    blobBody?: Promise<Blob>;
    /**
     * NODEJS ONLY
     *
     * The response body as a node.js Readable stream.
     * Always `undefined` in the browser.
     */
    readableStreamBody?: NodeJS.ReadableStream;
};

/** Optional parameters. */
export interface UploadUserDriveOptionalParams extends coreClient.OperationOptions {
    /** Binary blob. */
    data?: coreRestPipeline.RequestBodyType;
}

/** Optional parameters. */
export interface ListUserProgressOptionalParams extends coreClient.OperationOptions {}

/** Contains response data for the listUserProgress operation. */
export type ListUserProgressResponse = UserProgress[];

/** Optional parameters. */
export interface GetUserProgressOptionalParams extends coreClient.OperationOptions {}

/** Contains response data for the getUserProgress operation. */
export type GetUserProgressResponse = UserProgress;

/** Optional parameters. */
export interface CreateOrUpdateUserProgressOptionalParams extends coreClient.OperationOptions {
    /** Describes the state of progress of a user through a learning course. */
    body?: UserProgress;
}

/** Contains response data for the createOrUpdateUserProgress operation. */
export type CreateOrUpdateUserProgressResponse = UserProgress;

/** Optional parameters. */
export interface UpdateUserProgressOptionalParams extends coreClient.OperationOptions {
    /** Describes the state of progress of a user through a learning course. */
    body?: UserProgress;
}

/** Contains response data for the updateUserProgress operation. */
export type UpdateUserProgressResponse = UserProgress;

/** Optional parameters. */
export interface DeleteUserProgressOptionalParams extends coreClient.OperationOptions {}

/** Optional parameters. */
export interface DeleteUserProgressDriveOptionalParams extends coreClient.OperationOptions {}

/** Optional parameters. */
export interface DownloadUserProgressDriveOptionalParams extends coreClient.OperationOptions {}

/** Contains response data for the downloadUserProgressDrive operation. */
export type DownloadUserProgressDriveResponse = {
    /**
     * BROWSER ONLY
     *
     * The response body as a browser Blob.
     * Always `undefined` in node.js.
     */
    blobBody?: Promise<Blob>;
    /**
     * NODEJS ONLY
     *
     * The response body as a node.js Readable stream.
     * Always `undefined` in the browser.
     */
    readableStreamBody?: NodeJS.ReadableStream;
};

/** Optional parameters. */
export interface UploadUserProgressDriveOptionalParams extends coreClient.OperationOptions {
    /** Binary blob. */
    data?: coreRestPipeline.RequestBodyType;
}

/** Optional parameters. */
export interface ListUserProjectsOptionalParams extends coreClient.OperationOptions {}

/** Contains response data for the listUserProjects operation. */
export type ListUserProjectsResponse = Project[];

/** Optional parameters. */
export interface GetUserProjectOptionalParams extends coreClient.OperationOptions {}

/** Contains response data for the getUserProject operation. */
export type GetUserProjectResponse = Project;

/** Optional parameters. */
export interface CreateOrUpdateUserProjectOptionalParams extends coreClient.OperationOptions {
    /** User project metadata. */
    body?: Project;
}

/** Contains response data for the createOrUpdateUserProject operation. */
export type CreateOrUpdateUserProjectResponse = Project;

/** Optional parameters. */
export interface DeleteUserProjectOptionalParams extends coreClient.OperationOptions {}

/** Contains response data for the deleteUserProject operation. */
export type DeleteUserProjectResponse = VsCodeEduClientDeleteUserProjectHeaders;

/** Optional parameters. */
export interface PublishUserProjectOptionalParams extends coreClient.OperationOptions {}

/** Contains response data for the publishUserProject operation. */
export type PublishUserProjectResponse = VsCodeEduClientPublishUserProjectHeaders;

/** Optional parameters. */
export interface UnpublishUserProjectOptionalParams extends coreClient.OperationOptions {}

/** Contains response data for the unpublishUserProject operation. */
export type UnpublishUserProjectResponse = VsCodeEduClientUnpublishUserProjectHeaders;

/** Optional parameters. */
export interface CvsCallbackUserProjectOptionalParams extends coreClient.OperationOptions {
    /** Response from CVS callback */
    body?: CvsValidationResponse;
    /** Key to takedown user project. */
    cvsResponseKey?: string;
}

/** Optional parameters. */
export interface TakedownUserProjectOptionalParams extends coreClient.OperationOptions {
    /** Key to takedown user project. */
    cvsResponseKey?: string;
}

/** Optional parameters. */
export interface GetFileFromProjectOptionalParams extends coreClient.OperationOptions {
    /** Key to takedown user project. */
    cvsResponseKey?: string;
}

/** Contains response data for the getFileFromProject operation. */
export type GetFileFromProjectResponse = {
    /** The parsed response body. */
    body: coreRestPipeline.RequestBodyType;
};

/** Optional parameters. */
export interface RestoreUserProjectOptionalParams extends coreClient.OperationOptions {}

/** Optional parameters. */
export interface CreateUserProjectSharedLinkOptionalParams extends coreClient.OperationOptions {}

/** Contains response data for the createUserProjectSharedLink operation. */
export type CreateUserProjectSharedLinkResponse = Project;

/** Optional parameters. */
export interface DeleteUserProjectSharedLinkOptionalParams extends coreClient.OperationOptions {}

/** Optional parameters. */
export interface DownloadUserProjectDriveOptionalParams extends coreClient.OperationOptions {}

/** Contains response data for the downloadUserProjectDrive operation. */
export type DownloadUserProjectDriveResponse = {
    /**
     * BROWSER ONLY
     *
     * The response body as a browser Blob.
     * Always `undefined` in node.js.
     */
    blobBody?: Promise<Blob>;
    /**
     * NODEJS ONLY
     *
     * The response body as a node.js Readable stream.
     * Always `undefined` in the browser.
     */
    readableStreamBody?: NodeJS.ReadableStream;
};

/** Optional parameters. */
export interface UploadUserProjectDriveOptionalParams extends coreClient.OperationOptions {
    /** Binary blob. */
    data?: coreRestPipeline.RequestBodyType;
}

/** Optional parameters. */
export interface GetImmersiveReaderConfigOptionalParams extends coreClient.OperationOptions {}

/** Contains response data for the getImmersiveReaderConfig operation. */
export type GetImmersiveReaderConfigResponse = ImmersiveReaderConfig;

/** Optional parameters. */
export interface GetOperationOptionalParams extends coreClient.OperationOptions {}

/** Contains response data for the getOperation operation. */
export type GetOperationResponse = Operation;

/** Optional parameters. */
export interface ListUserCurriculaOptionalParams extends coreClient.OperationOptions {}

/** Contains response data for the listUserCurricula operation. */
export type ListUserCurriculaResponse = Curriculum[];

/** Optional parameters. */
export interface GetUserCurriculumOptionalParams extends coreClient.OperationOptions {}

/** Contains response data for the getUserCurriculum operation. */
export type GetUserCurriculumResponse = Curriculum;

/** Optional parameters. */
export interface CreateOrUpdateUserCurriculumOptionalParams extends coreClient.OperationOptions {
    /** Curriculum metadata. */
    body?: Curriculum;
}

/** Contains response data for the createOrUpdateUserCurriculum operation. */
export type CreateOrUpdateUserCurriculumResponse = VsCodeEduClientCreateOrUpdateUserCurriculumHeaders;

/** Optional parameters. */
export interface DeleteUserCurriculumOptionalParams extends coreClient.OperationOptions {}

/** Contains response data for the deleteUserCurriculum operation. */
export type DeleteUserCurriculumResponse = VsCodeEduClientDeleteUserCurriculumHeaders;

/** Optional parameters. */
export interface PublishUserCurriculumOptionalParams extends coreClient.OperationOptions {}

/** Contains response data for the publishUserCurriculum operation. */
export type PublishUserCurriculumResponse = VsCodeEduClientPublishUserCurriculumHeaders;

/** Optional parameters. */
export interface UnpublishUserCurriculumOptionalParams extends coreClient.OperationOptions {}

/** Contains response data for the unpublishUserCurriculum operation. */
export type UnpublishUserCurriculumResponse = VsCodeEduClientUnpublishUserCurriculumHeaders;

/** Optional parameters. */
export interface ListStandardGroupsOptionalParams extends coreClient.OperationOptions {}

/** Contains response data for the listStandardGroups operation. */
export type ListStandardGroupsResponse = StandardGroup[];

/** Optional parameters. */
export interface GetStandardGroupOptionalParams extends coreClient.OperationOptions {}

/** Contains response data for the getStandardGroup operation. */
export type GetStandardGroupResponse = StandardGroup;

/** Optional parameters. */
export interface CreateOrUpdateStandardGroupOptionalParams extends coreClient.OperationOptions {
    /** Group of education standards. */
    body?: StandardGroup;
}

/** Contains response data for the createOrUpdateStandardGroup operation. */
export type CreateOrUpdateStandardGroupResponse = StandardGroup;

/** Optional parameters. */
export interface UpdateStandardGroupOptionalParams extends coreClient.OperationOptions {
    /** Group of education standards. */
    body?: StandardGroup;
}

/** Contains response data for the updateStandardGroup operation. */
export type UpdateStandardGroupResponse = StandardGroup;

/** Optional parameters. */
export interface DeleteStandardGroupOptionalParams extends coreClient.OperationOptions {}

/** Optional parameters. */
export interface ListStandardsOptionalParams extends coreClient.OperationOptions {}

/** Contains response data for the listStandards operation. */
export type ListStandardsResponse = Standard[];

/** Optional parameters. */
export interface GetStandardOptionalParams extends coreClient.OperationOptions {}

/** Contains response data for the getStandard operation. */
export type GetStandardResponse = Standard;

/** Optional parameters. */
export interface CreateOrUpdateStandardOptionalParams extends coreClient.OperationOptions {
    /** Education standard. */
    body?: Standard;
}

/** Contains response data for the createOrUpdateStandard operation. */
export type CreateOrUpdateStandardResponse = Standard;

/** Optional parameters. */
export interface UpdateStandardOptionalParams extends coreClient.OperationOptions {
    /** Education standard. */
    body?: Standard;
}

/** Contains response data for the updateStandard operation. */
export type UpdateStandardResponse = Standard;

/** Optional parameters. */
export interface DeleteStandardOptionalParams extends coreClient.OperationOptions {}

/** Optional parameters. */
export interface ListLearningPathsOptionalParams extends coreClient.OperationOptions {}

/** Contains response data for the listLearningPaths operation. */
export type ListLearningPathsResponse = LearningPath[];

/** Optional parameters. */
export interface GetLearningPathOptionalParams extends coreClient.OperationOptions {}

/** Contains response data for the getLearningPath operation. */
export type GetLearningPathResponse = LearningPath;

/** Optional parameters. */
export interface CreateOrUpdateLearningPathOptionalParams extends coreClient.OperationOptions {
    /** Progression of courses on a particular subject. */
    body?: LearningPath;
}

/** Contains response data for the createOrUpdateLearningPath operation. */
export type CreateOrUpdateLearningPathResponse = LearningPath;

/** Optional parameters. */
export interface UpdateLearningPathOptionalParams extends coreClient.OperationOptions {
    /** Progression of courses on a particular subject. */
    body?: LearningPath;
}

/** Contains response data for the updateLearningPath operation. */
export type UpdateLearningPathResponse = LearningPath;

/** Optional parameters. */
export interface DeleteLearningPathOptionalParams extends coreClient.OperationOptions {}

/** Optional parameters. */
export interface ListCoursesOptionalParams extends coreClient.OperationOptions {}

/** Contains response data for the listCourses operation. */
export type ListCoursesResponse = Course[];

/** Optional parameters. */
export interface GetCourseOptionalParams extends coreClient.OperationOptions {}

/** Contains response data for the getCourse operation. */
export type GetCourseResponse = Course;

/** Optional parameters. */
export interface CreateOrUpdateCourseOptionalParams extends coreClient.OperationOptions {
    /** Learning course metadata. */
    body?: Course;
}

/** Contains response data for the createOrUpdateCourse operation. */
export type CreateOrUpdateCourseResponse = Course;

/** Optional parameters. */
export interface UpdateCourseOptionalParams extends coreClient.OperationOptions {
    /** Learning course metadata. */
    body?: Course;
}

/** Contains response data for the updateCourse operation. */
export type UpdateCourseResponse = Course;

/** Optional parameters. */
export interface DeleteCourseOptionalParams extends coreClient.OperationOptions {}

/** Contains response data for the deleteCourse operation. */
export type DeleteCourseResponse = VsCodeEduClientDeleteCourseHeaders;

/** Optional parameters. */
export interface DeleteCourseDriveOptionalParams extends coreClient.OperationOptions {}

/** Optional parameters. */
export interface DownloadCourseDriveOptionalParams extends coreClient.OperationOptions {}

/** Contains response data for the downloadCourseDrive operation. */
export type DownloadCourseDriveResponse = {
    /**
     * BROWSER ONLY
     *
     * The response body as a browser Blob.
     * Always `undefined` in node.js.
     */
    blobBody?: Promise<Blob>;
    /**
     * NODEJS ONLY
     *
     * The response body as a node.js Readable stream.
     * Always `undefined` in the browser.
     */
    readableStreamBody?: NodeJS.ReadableStream;
};

/** Optional parameters. */
export interface UploadCourseDriveOptionalParams extends coreClient.OperationOptions {
    /** Binary blob. */
    data?: coreRestPipeline.RequestBodyType;
}

/** Optional parameters. */
export interface ListCourseUnitsOptionalParams extends coreClient.OperationOptions {}

/** Contains response data for the listCourseUnits operation. */
export type ListCourseUnitsResponse = Unit[];

/** Optional parameters. */
export interface GetUnitOptionalParams extends coreClient.OperationOptions {}

/** Contains response data for the getUnit operation. */
export type GetUnitResponse = Unit;

/** Optional parameters. */
export interface CreateOrUpdateUnitOptionalParams extends coreClient.OperationOptions {
    /** Course unit metadata. */
    body?: Unit;
}

/** Contains response data for the createOrUpdateUnit operation. */
export type CreateOrUpdateUnitResponse = Unit;

/** Optional parameters. */
export interface UpdateUnitOptionalParams extends coreClient.OperationOptions {
    /** Course unit metadata. */
    body?: Unit;
}

/** Contains response data for the updateUnit operation. */
export type UpdateUnitResponse = Unit;

/** Optional parameters. */
export interface DeleteUnitOptionalParams extends coreClient.OperationOptions {}

/** Optional parameters. */
export interface DeletePackageDriveOptionalParams extends coreClient.OperationOptions {}

/** Optional parameters. */
export interface DownloadPackageDriveOptionalParams extends coreClient.OperationOptions {}

/** Contains response data for the downloadPackageDrive operation. */
export type DownloadPackageDriveResponse = {
    /**
     * BROWSER ONLY
     *
     * The response body as a browser Blob.
     * Always `undefined` in node.js.
     */
    blobBody?: Promise<Blob>;
    /**
     * NODEJS ONLY
     *
     * The response body as a node.js Readable stream.
     * Always `undefined` in the browser.
     */
    readableStreamBody?: NodeJS.ReadableStream;
};

/** Optional parameters. */
export interface UploadPackageDriveOptionalParams extends coreClient.OperationOptions {
    /** Binary blob. */
    data?: coreRestPipeline.RequestBodyType;
}

/** Optional parameters. */
export interface UploadContentOptionalParams extends coreClient.OperationOptions {
    /** Binary blob. */
    data?: coreRestPipeline.RequestBodyType;
}

/** Optional parameters. */
export interface UploadContentBlobOptionalParams extends coreClient.OperationOptions {
    /** Binary blob. */
    data?: coreRestPipeline.RequestBodyType;
}

/** Optional parameters. */
export interface GetConfigOptionalParams extends coreClient.OperationOptions {}

/** Contains response data for the getConfig operation. */
export type GetConfigResponse = { [propertyName: string]: string };

/** Optional parameters. */
export interface UpdateConfigOptionalParams extends coreClient.OperationOptions {
    /** Service configuration properties (dictionary string -> string). */
    body?: { [propertyName: string]: string };
}

/** Contains response data for the updateConfig operation. */
export type UpdateConfigResponse = { [propertyName: string]: string };

/** Optional parameters. */
export interface PatchConfigOptionalParams extends coreClient.OperationOptions {
    /** Service configuration properties (dictionary string -> string). */
    body?: { [propertyName: string]: string };
}

/** Contains response data for the patchConfig operation. */
export type PatchConfigResponse = { [propertyName: string]: string };

/** Optional parameters. */
export interface ReportAbuseOptionalParams extends coreClient.OperationOptions {
    /** Details on a reported piece of user generated content */
    body?: UserContentReport;
}

/** Optional parameters. */
export interface DeleteAppOptionalParams extends coreClient.OperationOptions {}

/** Optional parameters. */
export interface DownloadAppFileOptionalParams extends coreClient.OperationOptions {}

/** Contains response data for the downloadAppFile operation. */
export type DownloadAppFileResponse = {
    /** The parsed response body. */
    body: coreRestPipeline.RequestBodyType;
};

/** Optional parameters. */
export interface DownloadAppOptionalParams extends coreClient.OperationOptions {}

/** Contains response data for the downloadApp operation. */
export type DownloadAppResponse = {
    /**
     * BROWSER ONLY
     *
     * The response body as a browser Blob.
     * Always `undefined` in node.js.
     */
    blobBody?: Promise<Blob>;
    /**
     * NODEJS ONLY
     *
     * The response body as a node.js Readable stream.
     * Always `undefined` in the browser.
     */
    readableStreamBody?: NodeJS.ReadableStream;
};

/** Optional parameters. */
export interface UploadAppOptionalParams extends coreClient.OperationOptions {
    /** Binary blob. */
    data?: coreRestPipeline.RequestBodyType;
}

/** Optional parameters. */
export interface TakedownAppOptionalParams extends coreClient.OperationOptions {}

/** Optional parameters. */
export interface RestoreAppOptionalParams extends coreClient.OperationOptions {}

/** Optional parameters. */
export interface ListProjectTemplatesOptionalParams extends coreClient.OperationOptions {}

/** Contains response data for the listProjectTemplates operation. */
export type ListProjectTemplatesResponse = ProjectTemplate[];

/** Optional parameters. */
export interface GetProjectTemplateOptionalParams extends coreClient.OperationOptions {}

/** Contains response data for the getProjectTemplate operation. */
export type GetProjectTemplateResponse = ProjectTemplate;

/** Optional parameters. */
export interface CreateOrUpdateProjectTemplateOptionalParams extends coreClient.OperationOptions {
    /** Project template metadata. */
    body?: ProjectTemplate;
}

/** Contains response data for the createOrUpdateProjectTemplate operation. */
export type CreateOrUpdateProjectTemplateResponse = ProjectTemplate;

/** Optional parameters. */
export interface DeleteProjectTemplateOptionalParams extends coreClient.OperationOptions {}

/** Optional parameters. */
export interface DeleteProjectTemplateDriveOptionalParams extends coreClient.OperationOptions {}

/** Optional parameters. */
export interface DownloadProjectTemplateDriveOptionalParams extends coreClient.OperationOptions {}

/** Contains response data for the downloadProjectTemplateDrive operation. */
export type DownloadProjectTemplateDriveResponse = {
    /**
     * BROWSER ONLY
     *
     * The response body as a browser Blob.
     * Always `undefined` in node.js.
     */
    blobBody?: Promise<Blob>;
    /**
     * NODEJS ONLY
     *
     * The response body as a node.js Readable stream.
     * Always `undefined` in the browser.
     */
    readableStreamBody?: NodeJS.ReadableStream;
};

/** Optional parameters. */
export interface UploadProjectTemplateDriveOptionalParams extends coreClient.OperationOptions {
    /** Binary blob. */
    data?: coreRestPipeline.RequestBodyType;
}

/** Optional parameters. */
export interface StartSessionOptionalParams extends coreClient.OperationOptions {}

/** Contains response data for the startSession operation. */
export type StartSessionResponse = {
    /** The parsed response body. */
    body: string;
};

/** Optional parameters. */
export interface EndSessionOptionalParams extends coreClient.OperationOptions {}

/** Optional parameters. */
export interface ExchangeSessionOptionalParams extends coreClient.OperationOptions {}

/** Contains response data for the exchangeSession operation. */
export type ExchangeSessionResponse = {
    /** The parsed response body. */
    body: string;
};

/** Optional parameters. */
export interface NewSessionOptionalParams extends coreClient.OperationOptions {}

/** Contains response data for the newSession operation. */
export type NewSessionResponse = {
    /** The parsed response body. */
    body: string;
};

/** Optional parameters. */
export interface NewOtacOptionalParams extends coreClient.OperationOptions {
    /** Code challenge for PKCE authentication */
    codeChallenge?: string;
}

/** Contains response data for the newOtac operation. */
export type NewOtacResponse = {
    /** The parsed response body. */
    body: string;
};

/** Optional parameters. */
export interface ExchangeOtacOptionalParams extends coreClient.OperationOptions {
    /** Code verifier for PKCE authentication */
    codeVerifier?: string;
}

/** Contains response data for the exchangeOtac operation. */
export type ExchangeOtacResponse = {
    /** The parsed response body. */
    body: string;
};

/** Optional parameters. */
export interface GetSharedLinkOptionalParams extends coreClient.OperationOptions {}

/** Contains response data for the getSharedLink operation. */
export type GetSharedLinkResponse = SharedLink;

/** Optional parameters. */
export interface DeleteSharedLinkOptionalParams extends coreClient.OperationOptions {}

/** Optional parameters. */
export interface GithubInstallStartOptionalParams extends coreClient.OperationOptions {}

/** Contains response data for the githubInstallStart operation. */
export type GithubInstallStartResponse = {
    /** The parsed response body. */
    body: string;
};

/** Optional parameters. */
export interface GithubInstallExchangeOptionalParams extends coreClient.OperationOptions {}

/** Optional parameters. */
export interface GithubUninstallOptionalParams extends coreClient.OperationOptions {}

/** Optional parameters. */
export interface VsCodeEduClientOptionalParams extends coreClient.ServiceClientOptions {
    /** server parameter */
    $host?: HostOptions;
    /** Overrides client endpoint. */
    endpoint?: string;
}
